"use client";
import React, { useRef, useState } from "react";
import ProductCard from "./ProductCard";
import Swal from "sweetalert2";
import api from "../../utils/api";
import axios from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface RFIDData {
  bookingDate: string;
  cardAmount: number;
  customerMobileNumber: string;
  customerName: string;
  numberOdAdults: string;
  numberOfChildren: string;
  status: string | null;
  ticketBookingId: number;
  ticketNumber: string;
  ticketRFIdCardRef: string;
  ticketUniqueRFIdNo: string;
}


const ProductGrid = () => {
  const [cardNumber, setCardNumber] = useState<string | null>(null);
  const [rfidData, setRfidData] = useState<RFIDData | null>(null);
  const [searchFields, setSearchFields] = useState({
    ticketNumber: "",
    customerMobileNumber: "",
    customerName: "",
  });
  const [isSearching, setIsSearching] = useState(false);
  const latestSearchRequestRef = React.useRef(0);
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeControllerRef = React.useRef<AbortController | null>(null);
  const lastQueryKeyRef = React.useRef<string>("");
  const manualInputRef = React.useRef<Record<string, boolean>>({
    ticketNumber: false,
    customerMobileNumber: false,
    customerName: false,
  });
  const [selectedProducts, setSelectedProducts] = useState<typeof products>([]);
  const [subTotalAmount, setSubTotalAmount] = useState<number>(0);
  const [totalClothes, setTotalClothes] = useState<number>(0);
   // State for cloth security charges
  const [clothSecurityCharges, setClothSecurityCharges] = React.useState<number>(0);
  const [gstPercent, setGstPercent] = useState<string>("0");
  const gstAmount = (subTotalAmount * parseFloat(gstPercent)) / 100 || 0;
  const grandTotalAmount = subTotalAmount + gstAmount + clothSecurityCharges;
  // const products = [
  //   // Male
  //   { id: 1, price: 2000,  size: "S", quantity: 10, gender: "Male" },
  //   { id: 2, price: 2500,  size: "M", quantity: 20, gender: "Male" },
  //   { id: 3, price: 3000,  size: "L", quantity: 15, gender: "Male" },
  //   { id: 4, price: 3500,  size: "XL", quantity: 8, gender: "Male" },
  //   { id: 5, price: 2000,  size: "XXL", quantity: 5, gender: "Male" },
  //   // Female
  //   { id: 6, price: 2100, size: "S", quantity: 12, gender: "Female" },
  //   { id: 7, price: 2600,  size: "M", quantity: 18, gender: "Female" },
  //   { id: 8, price: 3100,  size: "L", quantity: 14, gender: "Female" },
  //   { id: 9, price: 3600,  size: "XL", quantity: 7, gender: "Female" },
  //   { id: 10, price: 2100,  size: "XXL", quantity: 6, gender: "Female" },
  //   // Baby Swim Suit
  //   { id: 11, price: 1200,  size: "XS", quantity: 10, gender: "Baby Swim Suit" },
  //   { id: 12, price: 1300,  size: "S", quantity: 8, gender: "Baby Swim Suit" },
  //   { id: 13, price: 1400,  size: "M", quantity: 6, gender: "Baby Swim Suit" },
  //   { id: 14, price: 1500,  size: "L", quantity: 4, gender: "Baby Swim Suit" }
  // ];
  const [products, setProducts] = useState<any[]>([]);

  const fetchClothMaster = async () => {
      try {
        const response = await api.get('/api/ClothMaster/GetAllClothMaster');
        // Convert response data to desired format
        const formattedData = (response.data || [])
          .filter((item: any) => item.clothQuantity > 0)
          .map((item: any) => ({
            id: item.clothId,
            price: item.clothPrice,
            size: item.clothSize,
            quantity: item.clothQuantity,
            gender: item.gender,
            securityCharges: item.securityCharges || 0 // Add securityCharges if available
          }));
        setProducts(formattedData);
      } catch (error) {
        setProducts([]);
      }
    };

  useEffect(() => {
    fetchClothMaster();
  }, []);

  const navigate = useNavigate();

  const handleReadRFID = async (fields: { ticketNumber: string; customerMobileNumber: string; customerName: string; }) => {
    const normalizedFields = {
      ticketNumber: fields.ticketNumber.trim(),
      customerMobileNumber: fields.customerMobileNumber.trim(),
      customerName: fields.customerName.trim(),
    };

    const hasSearchValue =
      normalizedFields.ticketNumber ||
      normalizedFields.customerMobileNumber ||
      normalizedFields.customerName;

    if (!hasSearchValue) {
      setRfidData(null);
      setSearchFields({
        ticketNumber: "",
        customerMobileNumber: "",
        customerName: "",
      });
      setIsSearching(false);
      return;
    }

    const queryKey = JSON.stringify(normalizedFields);
    if (lastQueryKeyRef.current === queryKey && activeControllerRef.current) {
      return;
    }

    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
    }

    lastQueryKeyRef.current = queryKey;
    const requestId = ++latestSearchRequestRef.current;
    const controller = new AbortController();
    activeControllerRef.current = controller;
    setIsSearching(true);

    try {
      const query = new URLSearchParams({
        ticketNumber: normalizedFields.ticketNumber,
        customerMobileNumber: normalizedFields.customerMobileNumber,
        customerName: normalizedFields.customerName,
      }).toString();

      const response = await api.get(`/api/RFID/ReadRFID?${query}`, {
        signal: controller.signal,
      });

      if (latestSearchRequestRef.current !== requestId || controller.signal.aborted) {
        return;
      }

      const responsePayload = response.data?.data ?? response.data?.result ?? response.data ?? {};
      const data = Array.isArray(responsePayload) ? responsePayload[0] ?? {} : responsePayload;

      const pickValue = (keys: string[]) => {
        for (const key of keys) {
          if (data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== "") {
            return String(data[key]);
          }
        }
        return "";
      };

      const apiTicketNumber = pickValue([
        "ticketNumber",
        "ticketNo",
        "ticketRFIdCardRef",
        "ticketRFIdRef",
        "ticketRFId",
        "ticketUniqueRFIdNo",
        "cardNumber",
      ]);
      const apiCustomerMobileNumber = pickValue([
        "customerMobileNumber",
        "mobileNumber",
        "contactNumber",
        "phoneNumber",
      ]);
      const apiCustomerName = pickValue([
        "customerName",
        "name",
        "fullName",
      ]);

      const normalizedTicketNumber =
        apiTicketNumber || normalizedFields.ticketNumber;
      const normalizedCustomerMobileNumber =
        apiCustomerMobileNumber || normalizedFields.customerMobileNumber;
      const normalizedCustomerName =
        apiCustomerName || normalizedFields.customerName;

      const filledSearchFields = {
        ticketNumber: normalizedTicketNumber,
        customerMobileNumber: normalizedCustomerMobileNumber,
        customerName: normalizedCustomerName,
      };

      setSearchFields((prev) => {
        const shouldUseApiValues =
          !manualInputRef.current.ticketNumber &&
          !manualInputRef.current.customerMobileNumber &&
          !manualInputRef.current.customerName;

        if (!shouldUseApiValues) {
          return {
            ticketNumber: manualInputRef.current.ticketNumber ? prev.ticketNumber : filledSearchFields.ticketNumber,
            customerMobileNumber: manualInputRef.current.customerMobileNumber
              ? prev.customerMobileNumber
              : filledSearchFields.customerMobileNumber,
            customerName: manualInputRef.current.customerName
              ? prev.customerName
              : filledSearchFields.customerName,
          };
        }

        return {
            ticketNumber:
              filledSearchFields.ticketNumber || prev.ticketNumber,
            customerMobileNumber:
              filledSearchFields.customerMobileNumber || prev.customerMobileNumber,
            customerName:
              filledSearchFields.customerName || prev.customerName
        };
      });

      if (
        !normalizedTicketNumber &&
        !normalizedCustomerMobileNumber &&
        !normalizedCustomerName
      ) {
        setRfidData({
          bookingDate: new Date().toISOString(),
          cardAmount: 0,
          customerMobileNumber: normalizedCustomerMobileNumber,
          customerName: normalizedCustomerName,
          numberOdAdults: "0",
          numberOfChildren: "0",
          status: null,
          ticketBookingId: 0,
          ticketNumber: normalizedTicketNumber,
          ticketRFIdCardRef: normalizedTicketNumber,
          ticketUniqueRFIdNo: normalizedTicketNumber,
        });
        return;
      }

      setRfidData({
        bookingDate: data.bookingDate ?? new Date().toISOString(),
        cardAmount: data.cardAmount ?? 0,
        customerMobileNumber: normalizedCustomerMobileNumber,
        customerName: normalizedCustomerName,
        numberOdAdults: data.numberOdAdults ?? data.numberOfAdults ?? "0",
        numberOfChildren: data.numberOfChildren ?? data.numberOfChildrens ?? "0",
        status: data.status ?? null,
        ticketBookingId: data.ticketBookingId ?? 0,
        ticketNumber: normalizedTicketNumber,
        ticketRFIdCardRef: data.ticketRFIdCardRef || normalizedTicketNumber,
        ticketUniqueRFIdNo: data.ticketUniqueRFIdNo || normalizedTicketNumber,
      });
      if(normalizedFields.customerMobileNumber || normalizedFields.customerName || normalizedFields.ticketNumber){
        setRfidData({
          bookingDate: data.bookingDate ?? new Date().toISOString(),
          cardAmount: data.cardAmount ?? 0,
          customerMobileNumber: normalizedFields.customerMobileNumber || normalizedCustomerMobileNumber,
          customerName: normalizedFields.customerName || normalizedCustomerName,
          numberOdAdults: data.numberOdAdults ?? data.numberOfAdults ?? "0",
          numberOfChildren: data.numberOfChildren ?? data.numberOfChildrens ?? "0",
          status: data.status ?? null,
          ticketBookingId: data.ticketBookingId ?? 0,
          ticketNumber: normalizedFields.ticketNumber || normalizedTicketNumber,
          ticketRFIdCardRef: normalizedFields.ticketNumber || normalizedTicketNumber,
          ticketUniqueRFIdNo: data.ticketUniqueRFIdNo || normalizedTicketNumber,
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        setRfidData(null);
      }
    } finally {
      if (latestSearchRequestRef.current === requestId && !controller.signal.aborted) {
        setIsSearching(false);
      }
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
    }
  };

  const handleSearchFieldFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as keyof typeof searchFields;
    if (searchFields[name] === "XXXXX") {
      setSearchFields((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSearchFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const nextFields = {
      ...searchFields,
      [name]: value,
    };

    manualInputRef.current = {
      ...manualInputRef.current,
      [name]: true,
    };

    setSearchFields(nextFields);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      handleReadRFID(nextFields);
    }, 350);
  };
  const handleSelectProduct = (product: typeof products[number]) => {
    setSelectedProducts((prev) =>
      prev.some((p) => p.id === product.id)
      ? prev.filter((p) => p.id !== product.id)
      : [...prev, { ...product, quantity: 1, clothSelectionId: 0, clothCalculationId: 0 }]
    );
    // Calculate total amount after selection
    const updatedProducts = selectedProducts.some((p) => p.id === product.id)
      ? selectedProducts.filter((p) => p.id !== product.id)
      : [...selectedProducts, { ...product, quantity: 1 }];
    // Calculate sub total amount
    const subTotalAmount = updatedProducts.reduce((sum, p) => sum + p.price * (p.quantity || 1), 0);
    setSubTotalAmount(subTotalAmount);
    // Calculate total security charges per clothes
    const totalSecurityCharges = updatedProducts.reduce((sum, p) => sum + p.securityCharges * (p.quantity || 1), 0);
    setClothSecurityCharges(totalSecurityCharges);
    // Calculate total quantity of clothes
    const totalQuantity = updatedProducts.reduce((sum, p) => sum + (p.quantity || 1), 0);
    setTotalClothes(totalQuantity);
  };

  // console.log("Total Clothes:", totalClothes);
  // console.log("Sub Total Amount:", subTotalAmount);
  // console.log("Selected Products:", selectedProducts);

   const confirmpay = async () => {
      try {
        // if(!cardNumber || cardNumber.toString().length !== 10) {
        //   setCardNumber(null);
        //   Swal.fire('Error!', 'Please enter a valid RF ID Card number.', 'error');
        //   return;
        // }
        if (selectedProducts.length === 0) {
          Swal.fire('Error!', 'Please select at least one cloth.', 'error');
          return;
        }
        // if(rfidData === null || rfidData?.ticketRFIdCardRef === null || rfidData?.ticketRFIdCardRef === undefined) {
        //   Swal.fire('Error!', 'Please scan a valid RF ID Card.', 'error');
        //   return;
        // }
        if (subTotalAmount <= 0) {
          Swal.fire('Error!', 'Sub Total Amount must be greater than zero.', 'error');
          return;
        }
        if (grandTotalAmount <= 0) {
          Swal.fire('Error!', 'Grand Total Amount must be greater than zero.', 'error');
          return;
        }
        // if(rfidData?.cardAmount === undefined || rfidData?.cardAmount === null || rfidData?.cardAmount <= 0) {
        //   Swal.fire('Error!', 'Insufficient Balance or Invalid Card Amount.', 'error');
        //   return;
        // }
        // if(rfidData && rfidData?.cardAmount > 0){
        //   if(rfidData.cardAmount < grandTotalAmount){
        //     Swal.fire('Error!', 'Insufficient Balance!!', 'error');
        //     return;
        //   }
        // }
        
        const result = await Swal.fire({
          title: 'Confirm Cloth Payment',
          html: `
          <div style="text-align:left">
            <p>Do you want to pay <b>₹${grandTotalAmount}</b> for the cloth(s)?</p>
            <p><b>Ticket No.:</b> ${rfidData?.ticketRFIdCardRef}</p>
            <p><b>Customer Number:</b> ${rfidData?.customerMobileNumber}</p>
            <p><b>Total Cloth(s):</b> ${totalClothes}</p>
            <p><b>Sub Total:</b> ₹${subTotalAmount}</p>
            <p><b>GST (${gstPercent}%):</b> ₹${gstAmount.toFixed(2)}</p>
            <p><b>Grand Total:</b> ₹${grandTotalAmount.toFixed(2)}</p>
          </div>
            `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, Pay',
          cancelButtonText: 'Cancel',
        });
  
        if (result.isConfirmed) {
          // Place recharge logic here
          try {
            // Here you can send the selected products to your API or perform any other action

            // const submittedData = {
            //   clothCalculationId: 0,
            //   totalCloth: totalClothes,
            //   selectedCardID: cardNumber,
            //   subTotalAmount: subTotalAmount,
            //   gst: gstAmount,
            //   grandTotalAmount: grandTotalAmount,
            //   ticketRFIdRef: "REF-SWP-4893",
            //   clothAssignDate: new Date().toISOString(),
            //   isLocked: true,
            //   clothSelections: selectedProducts.map((product) => ({
            //   clothSelectionId: 0,
            //   gender: product.gender.toLowerCase(),
            //   size: product.size.toLowerCase(),
            //   clothQuantity: product.quantity ?? 1,
            //   clothId: product.id,
            //   clothCalculationId: 0,
            //   clothPrice: product.price
            //   }))
            // };
            const submittedData = {
              clothCalculationId: 0,
              totalCloth: totalClothes,
              subTotalAmount: subTotalAmount,
              gst: gstAmount,
              grandTotalAmount: grandTotalAmount,
              isLocked: true,
              ticketRFIdRef: rfidData?.ticketRFIdCardRef, // or make this dynamic if needed rfidData?.ticketRFIdCardRef
              selectedCardID: cardNumber,
              clothAssignDate: new Date().toISOString(),
              refundableMoney: clothSecurityCharges.toFixed(2),
              staffUserid: JSON.parse(localStorage.getItem('user')!).userId,
              counterid: JSON.parse(localStorage.getItem('user')!).counterid,
              customerName: rfidData?.customerName || '',
              customerMobileNumber: rfidData?.customerMobileNumber || '',
              clothSelections: selectedProducts.map(product => ({
                clothSelectionId: 0,
                gender: product.gender,
                size: product.size,
                clothQuantity: product.quantity ?? 1,
                clothId: product.id,
                clothCalculationId: 0,
                clothPrice: product.price
              }))
            };

            // console.log("Submitted Data:", submittedData);
            try {
              const response = await api.post(
                '/api/ClothCalculation/AddClothCalculation',
                submittedData,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': '*/*'
                  }
                }
              );

              // console.log("✅ API response:", response.data);
              if (response.status === 200) {
                setSelectedProducts([]);
                setSubTotalAmount(0);
                setTotalClothes(0);
                setGstPercent("0");
                setCardNumber(null);
                setSearchFields({ ticketNumber: "", customerMobileNumber: "", customerName: "" });
                setRfidData(null);
                fetchClothMaster();
                Swal.fire({
                  icon: 'success',
                  title: 'Cloth Assigned Successfully',
                  text: 'Your cloth has been assigned successfully.',
                }).then(() => {
                  navigate(`/print-cloth-invoice/${response.data.clothCalculationId}`);
                });
              }else{
                Swal.fire({
                  icon: 'error',
                  title: 'Cloth Assign Failed',
                  text: 'Failed to assign cloth',
                });
              }
            } catch (error) {
              if (error && typeof error === "object" && "response" in error) {
                // @ts-ignore
                // console.error("❌ API error:", error.response?.data || error.message);
                Swal.fire({
                  icon: 'error',
                  title: 'Cloth Assign Failed',
                  text: 'Failed to assign cloth',
                });
              } else {
                // console.error("❌ API error:", error);
                Swal.fire({
                  icon: 'error',
                  title: 'Cloth Assign Failed',
                  text: 'Failed to assign cloth',
                });
              }
            }
            // Here you can send the submittedData to your API or perform any other action
            // Double-check API requirements for submittedData fields and types
            // const response = await api.post('/api/ClothCalculation/AddClothCalculation', JSON.stringify(submittedData), {
            //   headers: {
            //     'Content-Type': 'application/json',
            //   },
            // });
            // if (response.status === 200) {
            //   Swal.fire({
            //     icon: 'success',
            //     title: 'Cloth Assigned Successfully',
            //     text: 'Your cloth has been assigned successfully.',
            //   });
            //   // Reset the form or perform any other action after successful assignment
              // setSelectedProducts([]);
              // setSubTotalAmount(0);
              // setTotalClothes(0);
              // setGstPercent("0");
            // } else {
            //   // Show API error details if available
              // Swal.fire({
              //   icon: 'error',
              //   title: 'Cloth Assign Failed',
              //   text: response.data?.message || 'Failed to assign cloth',
              // });
            //   throw new Error('Failed to assign cloth');
            // }
          } catch (error) {
            Swal.fire({
              icon: 'error',
              title: 'Cloth Assign Failed',
              text: error instanceof Error ? error.message : 'Something went wrong while Cloth Assign.',
            });
          }
          
        }
      } catch (error) {
        Swal.fire('Error!', 'Failed to assign the cloth.', 'error');
      }
    }

  const renderRow = () => (
    <div className="row gx-4 gy-4 mb-4">
      {products.length === 0 ? (
        <div className="col-12 text-center text-muted py-5">
          No Cloth found
        </div>
      ) : (
        products.map((product) => (
          <div
            className={`col-md-3 col-sm-6`}
            key={product.id}
            onClick={() => handleSelectProduct(product)}
          >
            <div
                style={{
                  border: selectedProducts.some((p) => p.id === product.id)
                    ? "2px solid #007bff"
                    : "",
                  borderRadius: "8px",
                  padding: "4px",
                  cursor: "pointer",
                  transition: "border 0.2s"
                }}
              >
                <ProductCard
                  id={product.id}
                  price={product.price}
                  size={product.size}
                  quantity={product.quantity}
                  gender={product.gender}
                />
              </div>
            </div>
          ))
      )}
    </div>
  );

  const refs = useRef<(HTMLElement | null)[]>([]);
  const focusNext = (index: number) => {
    refs.current[index + 1]?.focus();
  };

  const focusPrev = (index: number) => {
    refs.current[index - 1]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    if (e.key === "ArrowRight" || e.key === "Enter") {
      e.preventDefault();
      focusNext(index);
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusPrev(index);
    }
  };

  return (
    <>
    <div className="col-lg-12 col-md-12">
      <article className="container-fluid mt-4">
        <header className="bg-white px-4 py-4 fs-5 fw-semibold text-dark border mb-4">
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                name="customerMobileNumber"
                placeholder="Customer Mobile Number"
                value={searchFields.customerMobileNumber}
                onFocus={handleSearchFieldFocus}
                onChange={handleSearchFieldChange}
                aria-label="Customer Mobile Number"
                ref={(el) => void (refs.current[0] = el)}
                onKeyDown={(e) => handleKeyDown(e, 0)}
              />
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                name="customerName"
                placeholder="Customer Name"
                value={searchFields.customerName}
                onFocus={handleSearchFieldFocus}
                onChange={handleSearchFieldChange}
                aria-label="Customer Name"
                ref={(el) => void (refs.current[1] = el)}
                onKeyDown={(e) => handleKeyDown(e, 1)}
              />
            </div>
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                name="ticketNumber"
                placeholder="Ticket Number"
                value={searchFields.ticketNumber}
                onFocus={handleSearchFieldFocus}
                onChange={handleSearchFieldChange}
                aria-label="Ticket Number"
                ref={(el) => void (refs.current[2] = el)}
                onKeyDown={(e) => handleKeyDown(e, 2)}
              />
            </div>
          </div>
          {isSearching && (
            <div className="text-muted small">Searching RFID data...</div>
          )}
          {/* <div className="input-group">
              <input
                  type="text"
                  className="form-control"
                  placeholder="Search cloth..."
                  aria-label="Search cloth by number"
                  aria-describedby="search-button"
              />
              <button className="btn btn-outline-secondary" type="button" id="search-button">
                  <i className="fa fa-search"></i>
              </button>
          </div> */}
        </header>
      </article>
    </div>
    <div className="col-lg-8 col-md-12">
      <article className="container-fluid mt-4">
        {renderRow()}
      </article>
    </div>
    {/* Ticket Details Column */}
    <div className="col-lg-4 col-md-12">
       <aside className="border border-5 border-warning bg-light text-dark small px-2 pt-4 pb-5 mt-3 w-100">
      <h2 className="text-center fw-semibold fs-5 mb-3">Ticket Details</h2>

      <div className="mx-auto" style={{ maxWidth: "200px" }}>
        <p>Ticket No.-{rfidData?.ticketRFIdCardRef}</p>
        <p className="mt-2">Customer Name-{rfidData?.customerName}</p>
        <p className="mt-2">Customer Number-{rfidData?.customerMobileNumber}</p>
        <p className="mt-2">Total Members - {Number(rfidData?.numberOdAdults) + Number(rfidData?.numberOfChildren)}</p>
        <p className="mt-2">No. of Adults - {rfidData?.numberOdAdults}</p>
        {/* <p className="mt-2">Kids - {rfidData?.numberOfChildren}</p>
        <p className="mt-2">Card Assign - xxxxxxxxxxxxxxxxx</p> */}
        {/* <p className="mt-2">Select Card ID - {cardNumber}</p> */}
        {/* <p className="mt-2">Balance - {rfidData?.cardAmount}</p> */}
      </div>

      <div className="mt-4">
        <img
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/1941f8873fe07e7c780902338a50e0fd17627aae?placeholderIfAbsent=true&apiKey=155088335b9245cc9e899591d6dc52ea"
          alt="Divider"
          className="img-fluid"
        />
      </div>

      <h3 className="text-center mt-3 fw-semibold">Calculations</h3>

      <div className="mt-3 ms-4">
        <p className="mb-2">Total Clothes - {totalClothes}</p>
        {selectedProducts.length === 0 ? (
          <div className="text-muted mb-2">No cloth selected</div>
        ) : (
          selectedProducts.map((product) => (
            <div className="d-flex align-items-center justify-content-between mb-2" key={product.id}>
              <span>
          Cloth-{product.size}-{product.gender[0]}
              </span>
              <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-secondary btn-sm px-2 py-0"
            onClick={() => {
              setSelectedProducts((prev) =>
                prev
            .map((p) =>
              p.id === product.id
                ? { ...p, quantity: Math.max((p.quantity || 1) - 1, 1) }
                : p
            )
            .filter((p) => (p.id === product.id ? (product.quantity || 1) > 1 : true))
              );
              // Update subtotal and quantity
              const updatedProducts = selectedProducts
                .map((p) =>
            p.id === product.id
              ? { ...p, quantity: Math.max((p.quantity || 1) - 1, 1) }
              : p
                )
                .filter((p) => (p.id === product.id ? (product.quantity || 1) > 1 : true));
              const subTotalAmount = updatedProducts.reduce((sum, p) => sum + p.price * (p.quantity || 1), 0);
              setSubTotalAmount(subTotalAmount);
              const totalSecurityCharges = updatedProducts.reduce((sum, p) => sum + p.securityCharges * (p.quantity || 1), 0);
              setClothSecurityCharges(totalSecurityCharges);
              const totalQuantity = updatedProducts.reduce((sum, p) => sum + (p.quantity || 1), 0);
              setTotalClothes(totalQuantity);
            }}
            disabled={(product.quantity || 1) <= 1}
          >
            -
          </button>
          <span>{product.quantity || 1}</span>
          <button
            className="btn btn-outline-secondary btn-sm px-2 py-0"
            onClick={() => {
                if (((product.quantity || 1) + 1 ) <= ((products.find(p => p.id === product.id)?.quantity))) {
                // Increase quantity if it does not exceed available stock
                  setSelectedProducts((prev) =>
                  prev.map((p) =>
                  p.id === product.id
                    ? { ...p, quantity: (p.quantity || 1) + 1 }
                    : p
                  )
                );
                // Update subtotal and quantity
                  const updatedProducts = selectedProducts.map((p) =>
                    p.id === product.id
                ? { ...p, quantity: (p.quantity || 1) + 1 }
                : p
                  );
                  const subTotalAmount = updatedProducts.reduce((sum, p) => sum + p.price * (p.quantity || 1), 0);
                  setSubTotalAmount(subTotalAmount);
                  const totalSecurityCharges = updatedProducts.reduce((sum, p) => sum + p.securityCharges * (p.quantity || 1), 0);
                  setClothSecurityCharges(totalSecurityCharges);
                  const totalQuantity = updatedProducts.reduce((sum, p) => sum + (p.quantity || 1), 0);
                  setTotalClothes(totalQuantity);
                } else {
                  Swal.fire('Error!', 'Selected quantity exceeds available stock.', 'error');
                }
            }}
          >
            +
          </button>
          <span>Rs. {product.price * (product.quantity || 1)}</span>
              </div>
            </div>
          ))
        )}
        <p className="mt-3">Sub Total Amount - {subTotalAmount}</p>
        <div className="d-flex align-items-center justify-content-between mt-2 mb-2" style={{ maxWidth: "160px" }}>
          <span className="me-2">GST -</span>
          <input
            type="text"
            className="form-control form-control-sm bg-white px-3 py-1 rounded-pill fw-normal"
            style={{ width: "100px" }}
            placeholder="GST %"
            min={0}
            max={100}
            value={gstPercent}
            onChange={(e) => setGstPercent(e.target.value)}
          />
        </div>
        <p>GST Amount - {gstAmount}</p>
        <p>Security Charges - {clothSecurityCharges.toFixed(2)}</p>
      </div>
        
      <div className="mt-3 ms-4">
        <p className="mt-2">Grand Total Amount - {grandTotalAmount}</p>
      </div>
      <div className="text-center mt-5">
        <button className="btn btn-primary px-5 py-2 rounded-pill fw-semibold" onClick={confirmpay}>
          Pay
        </button>
      </div>
    </aside>
    </div>
    </>
  );
};

export default ProductGrid;
